import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { useMutation } from "@tanstack/react-query";
import { authApi } from "@/lib/api";
import { queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AnimatedSection } from "@/components/ui/animated-section";
import { Eye, EyeOff, FileText, Wand2, Users, BarChart } from "lucide-react";

type AuthMode = "login" | "register" | "forgot";

export default function AuthPage() {
  const navigate = useNavigate();
  const { user, isLoading } = useAuth();
  const { toast } = useToast();
  const [authMode, setAuthMode] = useState<AuthMode>("login");
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    firstName: "",
    lastName: "",
  });

  // Check for OAuth error in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get("error");
    if (error) {
      toast({
        title: "Authentication Failed",
        description: error === "oauth_failed" ? "OAuth login failed. Please try again." : error,
        variant: "destructive",
      });
      // Clean up URL parameter
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [toast]);

  // Redirect if already logged in
  useEffect(() => {
    if (!isLoading && user) {
      navigate("/");
    }
  }, [user, isLoading, navigate]);

  const loginMutation = useMutation({
    mutationFn: async (credentials: { email: string; password: string }) => {
      return authApi.login(credentials.email, credentials.password);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["/api/auth/user"], data.user);
      toast({
        title: "Welcome back!",
        description: "You have been successfully logged in.",
      });
      navigate("/");
    },
    onError: (error: Error) => {
      toast({
        title: "Login failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const registerMutation = useMutation({
    mutationFn: async (userData: { 
      email: string; 
      password: string; 
      firstName: string; 
      lastName: string 
    }) => {
      return authApi.register(userData.email, userData.password, userData.firstName, userData.lastName);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["/api/auth/user"], data.user);
      toast({
        title: "Account created!",
        description: "Your account has been created successfully.",
      });
      navigate("/");
    },
    onError: (error: Error) => {
      toast({
        title: "Registration failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const forgotPasswordMutation = useMutation({
    mutationFn: async (email: string) => {
      return authApi.forgotPassword(email);
    },
    onSuccess: () => {
      toast({
        title: "Reset email sent",
        description: "Check your email for password reset instructions.",
      });
      setAuthMode("login");
    },
    onError: (error: Error) => {
      toast({
        title: "Reset failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (authMode === "login") {
      loginMutation.mutate({
        email: formData.email,
        password: formData.password,
      });
    } else if (authMode === "register") {
      if (formData.password !== formData.confirmPassword) {
        toast({
          title: "Password mismatch",
          description: "Passwords do not match",
          variant: "destructive",
        });
        return;
      }
      registerMutation.mutate({
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
      });
    } else if (authMode === "forgot") {
      forgotPasswordMutation.mutate(formData.email);
    }
  };

  const handleGoogleLogin = () => {
    // Redirect to Google OAuth
    window.location.href = authApi.getGoogleOAuthUrl();
  };

  const isLoading_ = loginMutation.isPending || registerMutation.isPending || forgotPasswordMutation.isPending;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-primary/5 to-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-primary/5 to-background flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <AnimatedSection delay={0.1} className="w-full max-w-md">
          <Card className="w-full shadow-xl bg-card/80 backdrop-blur-sm border-border/50">
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-bold text-center">
                {authMode === "login" && "Welcome back"}
                {authMode === "register" && "Create account"}
                {authMode === "forgot" && "Reset password"}
              </CardTitle>
              <p className="text-muted-foreground text-center">
                {authMode === "login" && "Sign in to your account"}
                {authMode === "register" && "Get started with SurvAI"}
                {authMode === "forgot" && "Enter your email to reset password"}
              </p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {authMode === "register" && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">First name</Label>
                      <Input
                        id="firstName"
                        type="text"
                        placeholder="John"
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                        autoComplete="given-name"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Last name</Label>
                      <Input
                        id="lastName"
                        type="text"
                        placeholder="Doe"
                        value={formData.lastName}
                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                        autoComplete="family-name"
                        required
                      />
                    </div>
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="john@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    autoComplete="email"
                    required
                  />
                </div>

                {authMode !== "forgot" && (
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        className="pr-10"
                        required
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <Eye className="h-4 w-4 text-muted-foreground" />
                        )}
                      </Button>
                    </div>
                  </div>
                )}

                {authMode === "register" && (
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm password</Label>
                    <Input
                      id="confirmPassword"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      autoComplete="new-password"
                      required
                    />
                  </div>
                )}

                <Button type="submit" className="w-full" disabled={isLoading_}>
                  {isLoading_ ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  ) : null}
                  {authMode === "login" && "Sign in"}
                  {authMode === "register" && "Create account"}
                  {authMode === "forgot" && "Send reset email"}
                </Button>
              </form>

              {/* OAuth Section */}
              {authMode !== "forgot" && (
                <>
                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-border" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-background px-2 text-muted-foreground">Or continue with</span>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="lg"
                    className="w-full gap-3 h-12 hover:bg-muted/50"
                    onClick={handleGoogleLogin}
                  >
                    <svg className="h-5 w-5" viewBox="0 0 24 24">
                      <path
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        fill="#4285F4"
                      />
                      <path
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        fill="#34A853"
                      />
                      <path
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        fill="#FBBC05"
                      />
                      <path
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        fill="#EA4335"
                      />
                    </svg>
                    Continue with Google
                  </Button>
                </>
              )}

              <div className="mt-6 text-center space-y-2">
                {authMode === "login" && (
                  <>
                    <button
                      type="button"
                      className="text-sm text-primary hover:underline"
                      onClick={() => setAuthMode("forgot")}
                    >
                      Forgot your password?
                    </button>
                    <div className="text-sm text-muted-foreground">
                      Don't have an account?{" "}
                      <button
                        type="button"
                        className="text-primary hover:underline"
                        onClick={() => setAuthMode("register")}
                      >
                        Sign up
                      </button>
                    </div>
                  </>
                )}
                
                {authMode === "register" && (
                  <div className="text-sm text-muted-foreground">
                    Already have an account?{" "}
                    <button
                      type="button"
                      className="text-primary hover:underline"
                      onClick={() => setAuthMode("login")}
                    >
                      Sign in
                    </button>
                  </div>
                )}
                
                {authMode === "forgot" && (
                  <button
                    type="button"
                    className="text-sm text-primary hover:underline"
                    onClick={() => setAuthMode("login")}
                  >
                    Back to sign in
                  </button>
                )}
              </div>
            </CardContent>
          </Card>
        </AnimatedSection>
      </div>

      {/* Right side - Hero */}
      <div className="hidden lg:flex lg:flex-1 relative overflow-hidden">
        {/* Grid pattern background */}
        <div className="absolute inset-0 grid-pattern opacity-20" />
        
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary/90 to-primary/70" />
        
        {/* Decorative blobs */}
        <div className="absolute top-20 left-10 w-72 h-72 hero-blob-primary rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 hero-blob-accent rounded-full blur-3xl opacity-50" />
        
        {/* Content */}
        <div className="relative z-10 flex items-center justify-center p-12 w-full">
          <div className="max-w-md text-center text-white">
            <AnimatedSection delay={0.2}>
              <div className="mb-8">
                <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6 backdrop-blur-sm">
                  <Wand2 className="h-10 w-10 text-white" />
                </div>
                <h2 className="text-3xl font-bold mb-4">Transform Documents to Digital Surveys</h2>
                <p className="text-primary-foreground/80 text-lg leading-relaxed">
                  Upload any survey document and watch AI automatically extract questions, 
                  classify types, and create editable digital surveys in seconds.
                </p>
              </div>
            </AnimatedSection>
            
            <AnimatedSection delay={0.4}>
              <div className="space-y-6">
                <div className="flex items-center text-left">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center mr-4 backdrop-blur-sm">
                    <FileText className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-semibold">Smart Document Processing</div>
                    <div className="text-primary-foreground/70 text-sm">Supports Word, PDF, Excel, and images</div>
                  </div>
                </div>
                
                <div className="flex items-center text-left">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center mr-4 backdrop-blur-sm">
                    <Users className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-semibold">Collaborative Editing</div>
                    <div className="text-primary-foreground/70 text-sm">Edit, preview, and share surveys</div>
                  </div>
                </div>
                
                <div className="flex items-center text-left">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center mr-4 backdrop-blur-sm">
                    <BarChart className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-semibold">Project Management</div>
                    <div className="text-primary-foreground/70 text-sm">Organize and track all your surveys</div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </div>
    </div>
  );
}
